// Google Analytics utilities - Enhanced tracking
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

// Device and browser detection
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  const isMobile = /Mobile|Android|iPhone|iPad|iPod/i.test(ua);
  const isTablet = /iPad|Android/i.test(ua) && !/Mobile/i.test(ua);

  return {
    device_type: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    browser: getBrowser(),
    os: getOS(),
    is_touch: 'ontouchstart' in window
  };
};

const getBrowser = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edge')) return 'Edge';
  return 'Other';
};

const getOS = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac')) return 'MacOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Other';
};

// Temporal/time-based information
const getTemporalInfo = () => {
  const now = new Date();
  const hour = now.getHours();
  const dayNum = now.getDay();

  // Time of day calculation
  let timeOfDay = 'night';
  if (hour >= 6 && hour < 12) timeOfDay = 'morning';
  else if (hour >= 12 && hour < 18) timeOfDay = 'afternoon';
  else if (hour >= 18 && hour < 22) timeOfDay = 'evening';

  return {
    day_of_week: now.toLocaleDateString('en-US', { weekday: 'long' }),
    day_of_week_es: now.toLocaleDateString('es-AR', { weekday: 'long' }),
    day_number: dayNum,
    hour_of_day: hour,
    time_of_day: timeOfDay,
    is_weekend: dayNum === 0 || dayNum === 6,
    is_business_hours: hour >= 9 && hour < 18 && dayNum >= 1 && dayNum <= 5,
    month: now.getMonth() + 1,
    year: now.getFullYear(),
    timezone_offset: now.getTimezoneOffset() / 60
  };
};

// Track custom events with automatic device info and temporal data
export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.gtag) {
    const deviceInfo = getDeviceInfo();
    const temporalInfo = getTemporalInfo();
    window.gtag('event', eventName, {
      event_category: 'Audiobook',
      ...deviceInfo,
      ...temporalInfo,
      ...parameters,
      timestamp: new Date().toISOString()
    });
  }
};

// Automatic geolocation by IP (no permissions needed)
export const initGeoTracking = async () => {
  try {
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();

    trackEvent('user_location', {
      country: data.country_name,
      country_code: data.country_code,
      city: data.city,
      region: data.region,
      timezone: data.timezone,
      latitude: data.latitude,
      longitude: data.longitude,
      event_category: 'User_Info'
    });
  } catch (error) {
    console.log('Geolocation tracking failed (silent):', error);
  }
};

// Track audiobook events
export const trackAudiobookEvent = {
  play: (chapter: string, language: string) => {
    trackEvent('play_audio', {
      chapter_name: chapter,
      audio_language: language,
      event_category: 'Audio'
    });
  },

  pause: (chapter: string, position: number, duration: number) => {
    const progressPercent = duration > 0 ? Math.round((position / duration) * 100) : 0;
    trackEvent('pause_audio', {
      chapter_name: chapter,
      position_seconds: position,
      duration_seconds: duration,
      progress_percent: progressPercent,
      event_category: 'Audio'
    });
  },

  chapterComplete: (chapter: string, language: string, timeToComplete: number) => {
    trackEvent('chapter_complete', {
      chapter_name: chapter,
      audio_language: language,
      time_to_complete_seconds: timeToComplete,
      event_category: 'Audio'
    });
  },

  chapterProgress: (chapter: string, progressPercent: number, position: number) => {
    // Track at 25%, 50%, 75% milestones
    if ([25, 50, 75].includes(progressPercent)) {
      trackEvent('chapter_progress', {
        chapter_name: chapter,
        progress_percent: progressPercent,
        position_seconds: position,
        event_category: 'Audio'
      });
    }
  },

  seek: (chapter: string, fromPosition: number, toPosition: number) => {
    const direction = toPosition > fromPosition ? 'forward' : 'backward';
    const seekDistance = Math.abs(toPosition - fromPosition);

    trackEvent('audio_seek', {
      chapter_name: chapter,
      from_position: fromPosition,
      to_position: toPosition,
      direction: direction,
      seek_distance_seconds: seekDistance,
      event_category: 'Audio'
    });
  },

  playbackSpeedChange: (chapter: string, newSpeed: number, oldSpeed: number) => {
    trackEvent('playback_speed_change', {
      chapter_name: chapter,
      new_speed: newSpeed,
      old_speed: oldSpeed,
      event_category: 'Audio'
    });
  },

  volumeChange: (type: 'chapter' | 'background', newVolume: number, oldVolume: number) => {
    trackEvent('volume_change', {
      volume_type: type,
      new_volume: newVolume,
      old_volume: oldVolume,
      event_category: 'Audio'
    });
  },

  bookmark: (chapter: string, position: number) => {
    trackEvent('create_bookmark', {
      chapter_name: chapter,
      position_seconds: position,
      event_category: 'User_Interaction'
    });
  },

  bookmarkDeleted: (chapter: string, position: number) => {
    trackEvent('delete_bookmark', {
      chapter_name: chapter,
      position_seconds: position,
      event_category: 'User_Interaction'
    });
  },

  bookmarkUsed: (chapter: string, position: number) => {
    trackEvent('use_bookmark', {
      chapter_name: chapter,
      position_seconds: position,
      event_category: 'User_Interaction'
    });
  },

  languageChange: (newLanguage: string, oldLanguage: string) => {
    trackEvent('change_language', {
      new_language: newLanguage,
      old_language: oldLanguage,
      event_category: 'User_Preference'
    });
  },

  playlistCreate: (playlistType: string, trackCount: number) => {
    trackEvent('create_playlist', {
      playlist_type: playlistType,
      track_count: trackCount,
      event_category: 'Music'
    });
  },

  backgroundMusicToggle: (enabled: boolean, musicService: string, chapter: string) => {
    trackEvent('background_music_toggle', {
      enabled: enabled,
      music_service: musicService,
      chapter_name: chapter,
      event_category: 'Music'
    });
  },

  chapterNavigation: (method: 'next_button' | 'previous_button' | 'chapter_list' | 'bookmark', fromChapter: string, toChapter: string) => {
    trackEvent('chapter_navigation', {
      navigation_method: method,
      from_chapter: fromChapter,
      to_chapter: toChapter,
      event_category: 'Navigation'
    });
  }
};

// Track errors
export const trackError = (errorType: string, errorMessage: string, errorContext?: Record<string, any>) => {
  trackEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage,
    ...errorContext,
    event_category: 'Error'
  });
};

// Track performance
export const trackPerformance = () => {
  if (typeof window !== 'undefined' && window.performance) {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

    if (navigation) {
      trackEvent('page_performance', {
        load_time: Math.round(navigation.loadEventEnd - navigation.fetchStart),
        dom_ready: Math.round(navigation.domContentLoadedEventEnd - navigation.fetchStart),
        first_paint: getFirstPaint(),
        event_category: 'Performance'
      });
    }
  }
};

const getFirstPaint = () => {
  const paintEntries = performance.getEntriesByType('paint');
  const firstPaint = paintEntries.find(entry => entry.name === 'first-contentful-paint');
  return firstPaint ? Math.round(firstPaint.startTime) : 0;
};

// Track session start
export const trackSessionStart = () => {
  trackEvent('session_start', {
    referrer: document.referrer || 'direct',
    event_category: 'Session'
  });
};

// Track session duration (call on page unload)
let sessionStartTime = Date.now();

export const trackSessionEnd = () => {
  const sessionDuration = Math.round((Date.now() - sessionStartTime) / 1000);
  trackEvent('session_end', {
    session_duration_seconds: sessionDuration,
    event_category: 'Session'
  });
};

// Track user engagement
export const trackEngagement = {
  firstInteraction: (action: string, timeToInteract: number) => {
    trackEvent('first_interaction', {
      first_action: action,
      time_to_interact_seconds: timeToInteract,
      event_category: 'Engagement'
    });
  },

  timeOnPage: (seconds: number) => {
    trackEvent('time_on_page', {
      time_seconds: seconds,
      event_category: 'Engagement'
    });
  },

  featureUsage: (featureName: string, usageCount: number) => {
    trackEvent('feature_usage', {
      feature_name: featureName,
      usage_count: usageCount,
      event_category: 'Engagement'
    });
  }
};

// Initialize analytics with measurement ID
export const initAnalytics = (measurementId: string) => {
  if (typeof window !== 'undefined') {
    // Update the measurement ID in gtag config
    window.gtag?.('config', measurementId, {
      anonymize_ip: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      send_page_view: true
    });

    // Track session start
    trackSessionStart();

    // Track device info on init
    const deviceInfo = getDeviceInfo();
    trackEvent('device_info', {
      ...deviceInfo,
      event_category: 'User_Info'
    });

    // Initialize geolocation tracking (silent, no prompts)
    initGeoTracking();

    // Track performance after page load
    if (document.readyState === 'complete') {
      trackPerformance();
    } else {
      window.addEventListener('load', trackPerformance);
    }

    // Track session end on page unload
    window.addEventListener('beforeunload', trackSessionEnd);
  }
};

// Track web vitals (Core Web Vitals)
export const trackWebVitals = (metric: any) => {
  trackEvent('web_vitals', {
    metric_name: metric.name,
    metric_value: Math.round(metric.value),
    metric_rating: metric.rating,
    event_category: 'Performance'
  });
};
